import { addScanFromScanner, loadSavedScans, sendMessage } from "actions";
import { SEND_DEVICE_MESSAGE } from "constants/actionTypes";

import {
  type Xrs2iEvent,
  Xrs2iEventType,
  type Xrs2iScanEvent,
  type Xrs2iErrorEvent,
  type Xrs2iSavedScansProgressEvent,
  type Xrs2iSavedScansCompleteEvent,
} from "../device/types";

export type DriverAction =
  | ReturnType<typeof addScanFromScanner>
  | ReturnType<typeof sendMessage>
  | ReturnType<typeof loadSavedScans>;

export type DriverActionResult = DriverAction[];

export interface DriverNotifier {
  error(message: string): void;
}

/**
 * Separates action-producing work from user notifications so the session can flush both in order.
 * The driver uses this to keep Redux updates deterministic while still surfacing scanner faults.
 */
export enum DriverEventType {
  ACTION,
  NOTIFY,
}

interface DriverActionEffect {
  type: DriverEventType.ACTION;
  action: DriverAction;
}

interface DriverNotificationEffect {
  type: DriverEventType.NOTIFY;
  message: string;
}

export type DriverEffect = DriverActionEffect | DriverNotificationEffect;

/**
 * Maps one scanner event into the Redux actions or notifications the app should see.
 * This keeps the protocol layer focused on device state while the adapter owns store-facing work.
 */
export function mapDeviceEventToDriverEffects(
  deviceId: string,
  event: Xrs2iEvent,
): DriverEffect[] {
  switch (event.type) {
    case Xrs2iEventType.SCAN:
      return mapScanEventToEffects(deviceId, event);

    case Xrs2iEventType.ERROR:
      return mapErrorEventToEffects(event);

    case Xrs2iEventType.INVALID_EID:
      return mapInvalidEidEventToEffects();

    case Xrs2iEventType.SAVED_SCANS_PROGRESS:
      return mapSavedScansProgressEventToEffects(deviceId, event);

    case Xrs2iEventType.SAVED_SCANS_COMPLETE:
      return mapSavedScansCompleteEventToEffects(deviceId, event);

    default:
      return assertNever(event);
  }
}

/**
 * Flushes queued scanner work into either Redux actions or user notifications.
 * Actions are returned to the caller, while notifications are emitted immediately so errors are visible.
 */
export function flushDriverEffects(
  effects: DriverEffect[],
  notifier: DriverNotifier,
): DriverActionResult {
  if (effects.length === 0) {
    return;
  }

  const actionEffects: DriverActionEffect[] = [];

  for (const effect of effects.splice(0, effects.length)) {
    if (isDriverNotificationEffect(effect)) {
      notifier.error((effect as DriverNotificationEffect).message);
      continue;
    }

    if (isDriverActionEffect(effect)) {
      actionEffects.push(effect as DriverActionEffect);
    }
  }

  if (actionEffects.length === 0) {
    return;
  }

  return orderDriverActions(actionEffects.map(effect => effect.action));
}

function orderDriverActions(actions: DriverAction[]): DriverAction[] {
  // Keep Redux actions ahead of send-message actions so state updates land before follow-up protocol writes.
  const sendActions = actions.filter(
    action => action.type === SEND_DEVICE_MESSAGE,
  );
  const domainActions = actions.filter(
    action => action.type !== SEND_DEVICE_MESSAGE,
  );
  return [...domainActions, ...sendActions];
}

function isDriverActionEffect(
  effect: DriverEffect,
): effect is DriverActionEffect {
  return effect.type === DriverEventType.ACTION;
}

function isDriverNotificationEffect(
  effect: DriverEffect,
): effect is DriverNotificationEffect {
  return effect.type === DriverEventType.NOTIFY;
}

function assertNever(value: never): never {
  throw new Error(`Unhandled XRS2i event: ${JSON.stringify(value)}`);
}

function mapScanEventToEffects(
  deviceId: string,
  event: Xrs2iScanEvent,
): DriverEffect[] {
  return [
    {
      type: DriverEventType.ACTION,
      action: addScanFromScanner(deviceId, {
        created: event.created,
        EID: event.eid,
        draftName: "",
      }),
    },
  ];
}

function mapErrorEventToEffects(event: Xrs2iErrorEvent): DriverEffect[] {
  return [
    {
      type: DriverEventType.NOTIFY,
      // Surface protocol errors as a toast so operators see scanner problems.
      message: `Error communicating with scanner (${event.code})`,
    },
  ];
}

function mapInvalidEidEventToEffects(): DriverEffect[] {
  return [
    {
      type: DriverEventType.NOTIFY,
      // Tell the operator that the scanner returned data we could not normalise into an EID.
      message: "Received invalid EID data from scanner",
    },
  ];
}

function mapSavedScansProgressEventToEffects(
  deviceId: string,
  event: Xrs2iSavedScansProgressEvent,
): DriverEffect[] {
  return [
    {
      type: DriverEventType.ACTION,
      // Keep partial session results in Redux as each session finishes so progress screens can update live.
      action: loadSavedScans(deviceId, true, event.importedScans, {
        append: true,
        isFinal: false,
        completedSessions: event.completedSessions,
        totalSessions: event.totalSessions,
      }),
    },
  ];
}

function mapSavedScansCompleteEventToEffects(
  deviceId: string,
  event: Xrs2iSavedScansCompleteEvent,
): DriverEffect[] {
  return [
    {
      type: DriverEventType.ACTION,
      // Mark the import as final once the scanner reports that every saved session has been read.
      action: loadSavedScans(deviceId, event.success, event.importedScans, {
        append: false,
        isFinal: true,
        completedSessions: event.completedSessions,
        totalSessions: event.totalSessions,
      }),
    },
  ];
}
